<script setup>
import { ref, watch, computed, onMounted, nextTick } from 'vue';
import SvgIcon from 'JIX/components/SvgIcon.vue';
import DsCheckSwitch from 'JIX/components/ds/DsCheckSwitch.vue';
import { __ } from 'JIX/gettext.js';
import 'icons/complete.svg';
import { isLoggedIn } from 'JIX/utils/login.js';
import { useStore } from 'vuex';
import BlockInteraction from 'JIX/components/BlockInteraction.vue';
import ApiConflictError from "JIX/errors/ApiConflictError.js";
import { updateJobagent, createJobagent } from 'JIX/jobagent/api.js';
import bootbox from 'bootbox';
import DsOptionsMenu from 'JIX/components/ds/DsOptionsMenu.vue';
import ApiValidationError from 'JIX/errors/ApiValidationError';
import RequestError from 'JIX/errors/RequestError';
import { isMobile } from 'JIX/utils.js';
import { processParams } from 'JIX/jobsearch/utils';

const store = useStore();

const jobagent_subscribed = ref(store.state.searchResponse.jobagent?.active);
const jobagent_switch_on = ref(jobagent_subscribed.value);
const jobagent_id = ref(store.state.searchResponse.jobagent?.id);
const jobagent_url = ref(store.state.searchResponse.jobagent?.url);
const jobagent_status_message = ref('');
const display_success_checkmark = ref(false);

const user_logged_in = ref(store.state.searchResponse.user_logged_in);
const email = ref(null);
const processing = ref(false);
const emailInput = ref(null);

function handleEnterMail() {
    newJobagent();
}

watch(jobagent_switch_on, async () => {
    processing.value = true;
    const loginResult = await isLoggedIn('user');
    processing.value = false;
    user_logged_in.value = loginResult;

    if (user_logged_in.value){
        if (jobagent_switch_on.value) {
            if (jobagent_id.value){
                //Subscribe to existing jobagent
                saveJobagent();
            } else {
                //Subscribe to new jobagent
                newJobagent();
            }
        } else if (jobagent_id.value){
            //Unsubscribe from jobagent
            saveJobagent();
        }
    } else if (jobagent_switch_on.value && !jobagent_subscribed.value){
        //Focus on email input
        await nextTick();
        emailInput.value.focus();
    }
});

async function newJobagent(){
    if (showApiValidationErrorsIfPresent()){
        return;
    }
    processing.value = true;
    const jobagentData = {
        id: null,
        enabled: true,
        search_params: processParams(store.state.searchQuery),
        frequency: 'daily',
        email: email.value
    };
    await createJobagent(jobagentData).then((response) => {
        jobagent_subscribed.value = true;
        jobagent_id.value = response.jobagent.id;
        jobagent_url.value = response.jobagent.edit_url;
        refreshStatusMessage();
    }).catch((error) => {
        processing.value = false;
        handleJobagentApiError(error);
    });
    processing.value = false;
}

async function saveJobagent(){
    if (showApiValidationErrorsIfPresent()){
        return;
    }
    processing.value = true;
    const updatedJobagent = {
        id: jobagent_id.value,
        enabled: jobagent_switch_on.value,
    };
    await updateJobagent(updatedJobagent).then(() => {
        jobagent_subscribed.value = jobagent_switch_on.value;
        refreshStatusMessage();
    }).catch((error) => {
        processing.value = false;
        handleJobagentApiError(error);
    });
    processing.value = false;
}

function handleJobagentApiError(error){
    jobagent_status_message.value = __('Der opstod en fejl');
    if (error instanceof ApiConflictError && error.error === 'duplicate_jobagent') {
        showDuplicateJobagentError();
    } else if (error instanceof ApiValidationError){
        showInvalidJobagentInputError();
    } else if (error instanceof RequestError && error.status === 404){
        showJobagentNotFoundError();
    } else {
        throw error;
    }
}

function showApiValidationErrorsIfPresent(){
    if (store.state.apiValidationErrors && store.state.apiValidationErrors.length > 0){
        bootbox.dialog({
            title: __("Fejl"),
            message: __("Der er ugyldige værdier i dine filtre: ") + store.state.apiValidationErrors[0].error_message,
            size: "large",
            onEscape: true,
            onBackdrop: true,
            centerVertical: true,
            buttons: {
                ok: {
                    label: __("Ok"),
                    className: 'btn-primary',
                }
            },
        });
        return true;
    }
    return false;
}

function showErrorDialog(errorMessage){
    bootbox.dialog({
        title: __("Fejl"),
        message: errorMessage,
        size: "large",
        onEscape: true,
        onBackdrop: true,
        centerVertical: true,
        buttons: {
            reload: {
                label: __("Genindlæs"),
                className: 'btn btn-primary',
                callback: function() {
                    location.reload();
                }
            },
        },
    });
}

function showJobagentNotFoundError(){
    showErrorDialog(__("Jobagenten kunne ikke findes"));
}

function showDuplicateJobagentError(){
    showErrorDialog(__("Du har allerede en Jobagent med samme søgeparametre"));
}

function showInvalidJobagentInputError(){
    //Todo: Open the filters and show the user where the error is
    showErrorDialog(__("Filtrene indeholdt ugyldige værdier"));
}

const menuItems = computed(() => [
    {
        key: 'toggle_subscription',
        label: jobagent_switch_on.value ? __("Afmeld Jobagent") : __("Tilmeld Jobagent"),
        onclick() {
            jobagent_switch_on.value = !jobagent_switch_on.value;
        }
    },
    {
        key: 'edit',
        label: __("Ret Jobagent"),
        url: jobagent_url.value,
        hidden: jobagent_id.value === undefined
    },
    {
        key: 'overview',
        label: __("Se Jobagenter"),
        url: store.state.jobagentRootUrl
    },
].filter(item => !item.hidden));

function refreshStatusMessage(){
    display_success_checkmark.value = false;
    if (jobagent_switch_on.value && !user_logged_in.value && !jobagent_subscribed.value){
        jobagent_status_message.value = __('Anmodning afsendt');
    } else if (jobagent_switch_on.value){
        display_success_checkmark.value = true;
        jobagent_status_message.value = __('Jobagent tilmeldt');
    } else if (!jobagent_switch_on.value && jobagent_id.value){
        jobagent_status_message.value = __('Få nye job sendt på mail');
    } else {
        jobagent_status_message.value = '';
    }
}

onMounted(() => {
    refreshStatusMessage();
});
</script>

<template>
    <div class="jobagent-banner">
        <BlockInteraction :request-in-progress="processing" />
        <div class="jobagent-banner__section-horizontal">
            <div v-if="!jobagent_status_message">
                <div v-if="isMobile">
                    <strong>{{ __('Få nye job sendt på mail') }}</strong>
                </div>
                <div v-else>
                    <strong>{{ __('Gem din søgning') }}</strong>
                    {{ __(' og få nye job sendt på mail') }}
                </div>
            </div>
            <div v-else>
                <SvgIcon
                    class="mr-2 complete-icon"
                    v-if="display_success_checkmark"
                    name="complete"
                    size="xs"
                />
                <strong>{{ jobagent_status_message }}</strong>
            </div>
            <div class="d-flex">
                <DsCheckSwitch
                    v-model="jobagent_switch_on"
                />
                <DsOptionsMenu
                    id="jobagent-menu"
                    :items="menuItems"
                    class="ml-3"
                    size="sm"
                />
            </div>
        </div>
        <form @submit.prevent="handleEnterMail" class="d-flex flex-column align-items-end" v-if="jobagent_switch_on && !user_logged_in && !jobagent_subscribed">
            <div class="d-flex flex-column w-100">
                <label class="control-label">{{ __('E-mail') }}</label>
                <input
                    ref="emailInput"
                    v-model="email"
                    type="email"
                    required
                    autocomplete="email"
                    :placeholder="__('Indtast e-mail som vi skal sende jobannoncer til')"
                    class="form-control"
                >
            </div>
            <button type="submit" class="btn btn-primary btn-sm mt-3">
                {{ __('Opret Jobagent') }}
            </button>
        </form>
    </div>
</template>